import { useState } from 'react';
import { ApiResponse } from '../types';

interface ProductInputProps {
  onProductAdded?: () => void;
}

/**
 * 产品输入组件
 * 用于添加要监控的TikTok产品
 */
const ProductInput: React.FC<ProductInputProps> = ({ onProductAdded }) => {
  const [productId, setProductId] = useState('');
  const [region, setRegion] = useState('US');
  const [loading, setLoading] = useState(false);

  // region选项配置
  const regionOptions = [
    { value: 'US', label: '🇺🇸 美国 (US)' },
    { value: 'GB', label: '🇬🇧 英国 (GB)' },
    { value: 'IE', label: '🇮🇪 爱尔兰 (IE)' },
    { value: 'ES', label: '🇪🇸 西班牙 (ES)' },
    { value: 'MX', label: '🇲🇽 墨西哥 (MX)' },
    { value: 'ID', label: '🇮🇩 印度尼西亚 (ID)' },
    { value: 'SG', label: '🇸🇬 新加坡 (SG)' },
    { value: 'MY', label: '🇲🇾 马来西亚 (MY)' },
    { value: 'PH', label: '🇵🇭 菲律宾 (PH)' },
    { value: 'TH', label: '🇹🇭 泰国 (TH)' },
    { value: 'VN', label: '🇻🇳 越南 (VN)' },
  ];

  // 提交监控产品
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!productId.trim()) {
      // 显示警告提示
      const toast = document.createElement('div');
      toast.className = 'toast toast-top toast-end';
      toast.innerHTML = `
        <div class="alert alert-warning">
          <span>⚠️ 请输入产品ID</span>
        </div>
      `;
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 3000);
      return;
    }

    try {
      setLoading(true);
      
      const response = await fetch('/api/monitor/products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ productId, region }),
      });

      // 检查响应状态
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      // 检查响应类型
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('服务器返回了非JSON格式的响应');
      }

      const result: ApiResponse = await response.json();

      if (result.success) {
        // 显示成功提示
        const toast = document.createElement('div');
        toast.className = 'toast toast-top toast-end';
        toast.innerHTML = `
          <div class="alert alert-success">
            <span>✅ ${result.message || '产品已添加到监控列表'}</span>
          </div>
        `;
        document.body.appendChild(toast);
        setTimeout(() => document.body.removeChild(toast), 3000);

        // 清空输入框，但保持region选择
        setProductId('');
        
        // 触发回调
        onProductAdded?.();
      } else {
        throw new Error(result.message || '添加失败');
      }
    } catch (error) {
              console.error('添加产品失败:', (error as Error).message);
      
      let errorMessage = '添加失败';
      
      if (error instanceof Error) {
        if (error.message.includes('HTTP 404')) {
          errorMessage = '服务器API路径不存在，请检查后端服务是否正常运行';
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = '无法连接到服务器，请检查后端服务是否启动（端口3001）';
        } else if (error.message.includes('Network')) {
          errorMessage = '网络连接失败，请检查网络设置';
        } else {
          errorMessage = error.message;
        }
      }
      
      // 显示错误提示
      const toast = document.createElement('div');
      toast.className = 'toast toast-top toast-end';
      toast.innerHTML = `
        <div class="alert alert-error">
          <span>❌ ${errorMessage}</span>
        </div>
      `;
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card bg-base-100 shadow-lg max-w-2xl">
      <div className="card-body p-4">
        <h3 className="card-title text-base mb-3">
          <span className="text-success">📊</span>
          监控设置
        </h3>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Region选择器 */}
          <div className="form-control">
            <label className="label py-1">
              <span className="label-text font-medium text-sm">选择地区/市场</span>
              <span className="label-text-alt text-xs text-base-content/60">TikTok Shop 地区</span>
            </label>
            <select
              className="select select-bordered select-sm"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              disabled={loading}
            >
              {regionOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* 产品ID输入和开始监控按钮 */}
          <div className="form-control">
            <label className="label py-1">
              <span className="label-text font-medium text-sm">TikTok产品ID</span>
              <span className="label-text-alt text-xs">
                <button
                  type="button"
                  className="link link-primary text-xs"
                  onClick={() => {
                    const modal = document.getElementById('help-modal');
                    if (modal instanceof HTMLDialogElement) {
                      modal.showModal();
                    }
                  }}
                >
                  使用示例ID
                </button>
              </span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                className="input input-bordered input-sm flex-1"
                placeholder="请输入产品ID"
                value={productId}
                onChange={(e) => setProductId(e.target.value.replace(/\D/g, ''))}
                disabled={loading}
              />
              <button
                type="submit"
                className={`btn btn-primary btn-sm ${loading ? 'loading' : ''}`}
                disabled={loading || !productId.trim()}
              >
                {loading ? (
                  <>
                    <span className="loading loading-spinner loading-xs"></span>
                    添加中
                  </>
                ) : (
                  <>
                    <span className="text-sm">🚀</span>
                    开始监控
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* 帮助模态框 */}
      <dialog id="help-modal" className="modal">
        <div className="modal-box">
          <h3 className="font-bold text-lg">产品ID示例</h3>
          <p className="py-4">
            您可以使用以下示例ID进行测试：
          </p>
          <div className="mockup-code">
            <pre data-prefix="$">
              <code>1729564508010418696</code>
            </pre>
          </div>
          <div className="modal-action">
            <button
              className="btn btn-primary"
              onClick={() => {
                setProductId('1729564508010418696');
                const modal = document.getElementById('help-modal');
                if (modal instanceof HTMLDialogElement) {
                  modal.close();
                }
              }}
            >
              使用此ID
            </button>
            <form method="dialog">
              <button className="btn">关闭</button>
            </form>
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default ProductInput; 